import { Box, Card, CardActionArea, CardContent, Typography, List, ListItem, ListItemText, Divider, CircularProgress, Alert, Chip, Accordion, AccordionSummary, AccordionDetails, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { customerApi } from '../services/api';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { SHOW_CUSTOMER_QR } from '../config/constants';

const SalesMenu: React.FC = () => {
  const navigate = useNavigate();
  const [latestCustomers, setLatestCustomers] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<boolean>(false);
  const [loaded, setLoaded] = useState<boolean>(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // İlk kez açıldığında veriyi getir (lazy load)
  useEffect(() => {
    const fetchLatest = async () => {
      if (!expanded || loaded) return;
      try {
        setLoading(true);
        const data = await customerApi.getLatestCustomersPerCode();
        setLatestCustomers(Array.isArray(data) ? data : []);
        setLoaded(true);
        setError(null);
      } catch (err: any) {
        setError(err?.message || 'Veri alınamadı');
      } finally {
        setLoading(false);
      }
    };
    fetchLatest();
  }, [expanded, loaded]);

  return (
    <Box sx={{
      minHeight: 'calc(100vh - 120px)',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: SHOW_CUSTOMER_QR ? 'flex-start' : 'center',
      alignItems: 'center',
      p: 2,
      gap: 2,
    }}>
      

      <Card sx={{ width: '100%', maxWidth: 480 }}>
        <CardActionArea onClick={() => navigate('/customers/create')}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="h5" component="div" gutterBottom>
              Yeni Müşteri
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Hızlıca yeni müşteri oluştur
            </Typography>
          </CardContent>
        </CardActionArea>
      </Card>

      {SHOW_CUSTOMER_QR && (
        <Card sx={{ width: '100%', maxWidth: 480 }}>
          <CardActionArea onClick={() => navigate('/customer-qr')}>
            <CardContent sx={{ textAlign: 'center', py: 6 }}>
              <Typography variant="h5" component="div" gutterBottom>
                Müşteri QR Link
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Müşteriye kendi kaydı için QR/link gönder
              </Typography>
            </CardContent>
          </CardActionArea>
        </Card>
      )}

      {/* Son 10 Müşteri (Tek satır/tek kod, veritabanı bazında varlık) */}
      <Accordion sx={{ width: '100%', maxWidth: 480 }} expanded={expanded} onChange={(_, isExpanded) => setExpanded(isExpanded)}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Typography variant="h6" component="div" sx={{ fontSize: '0.84rem' }}>
            Son 10 Müşteri
          </Typography>
          {!expanded && loaded && latestCustomers.length > 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
              ({latestCustomers.length})
            </Typography>
          )}
        </AccordionSummary>
        <AccordionDetails>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : latestCustomers.length === 0 ? (
            <Typography variant="body2" color="text.secondary">Kayıt bulunamadı</Typography>
          ) : (
            <TableContainer component={Paper} sx={{ maxHeight: 340 }}>
              <Table size="small" stickyHeader sx={{
                '& td, & th': { fontSize: '0.66rem', py: 0.25 },
                '& th': { bgcolor: 'background.paper' }
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.66rem', textAlign: 'center', width: 28, px: 0.5 }} title="V3_StDeri">STD</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.66rem', textAlign: 'center', width: 30, px: 0.5 }} title="V3_StDeriLTD">STDL</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.66rem', textAlign: 'center', width: 30, px: 0.5 }} title="V3_SerkanCanta">SÇT</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.66rem', textAlign: 'center', width: 32, px: 0.5 }} title="V3_SerkanCantaLTD">SÇTL</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem' }}>Code</TableCell>
                    <TableCell sx={{ fontWeight: 600, fontSize: '0.7rem', width: isMobile ? 'auto' : '30%' }}>Müşteri</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {([...latestCustomers]
                    .map((x: any) => ({
                      ...x,
                      __code: x.CurrAccCode || x.currAccCode || x.code,
                      __last4: (x.CurrAccCode || x.currAccCode || x.code || '').toString().slice(-4)
                    }))
                    .sort((a: any, b: any) => (b.__last4 || '').localeCompare(a.__last4 || ''))
                    .slice(0, 10)
                    .map((c: any, idx: number) => {
                      const code = c.__code || '-';
                      const name = c.CurrAccDescription || c.currAccDescription || '-';
                      const up = (s: any) => (s ? String(s).toLocaleUpperCase('tr-TR') : '-');
                      const toBool = (v: any) => v === true || v === 1 || v === '1';
                      const yes = (v: any) => toBool(v);
                      const flag = (obj: any, pascal: string, camel: string) => yes(obj[pascal] ?? obj[camel]);
                      const fSTD = flag(c, 'InV3_StDeri', 'inV3_StDeri');
                      const fSTDL = flag(c, 'InV3_StDeriLTD', 'inV3_StDeriLTD');
                      const fSCT = flag(c, 'InV3_SerkanCanta', 'inV3_SerkanCanta');
                      const fSCTL = flag(c, 'InV3_SerkanCantaLTD', 'inV3_SerkanCantaLTD');
                      const cellSx = (on: boolean) => ({ textAlign: 'center', fontWeight: 700, color: on ? 'success.main' : 'error.main', px: 0.5 });
                      return (
                        <TableRow
                          key={`${code}-${idx}`}
                          hover
                          sx={{ cursor: 'pointer' }}
                          onClick={() => navigate(`/customers/${code}`)}
                        >
                          <TableCell sx={{ ...cellSx(fSTD), width: 28 }}>{fSTD ? '✓' : 'x'}</TableCell>
                          <TableCell sx={{ ...cellSx(fSTDL), width: 30 }}>{fSTDL ? '✓' : 'x'}</TableCell>
                          <TableCell sx={{ ...cellSx(fSCT), width: 30 }}>{fSCT ? '✓' : 'x'}</TableCell>
                          <TableCell sx={{ ...cellSx(fSCTL), width: 32 }}>{fSCTL ? '✓' : 'x'}</TableCell>
                          <TableCell>
                            <Typography
                              component="span"
                              sx={{ fontWeight: 700, color: 'primary.main', fontSize: '0.76rem', fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace' }}
                              title={`Tam kod: ${code}`}
                            >
                              {c.__last4 || code}
                            </Typography>
                          </TableCell>
                          <TableCell title={name} sx={{ width: isMobile ? 'auto' : '30%' }}>
                            {/* 1. Satır: Müşteri adı (tek satır, ellipsis) */}
                            <Typography sx={{
                              fontSize: '0.7rem',
                              maxWidth: isMobile ? 220 : '100%',
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              lineHeight: 1.15
                            }}>
                              {name}
                            </Typography>
                            {/* 2. Satır: Konum (tek satır, renkli) */}
                            <Box sx={{ display: 'block', mt: 0.25, maxWidth: isMobile ? 220 : '100%', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', fontSize: '0.7rem' }}>
                              <Box component="span" sx={{ color: 'success.main', fontWeight: 700 }}>
                                {up(c.SalespersonName ?? c.salespersonName)}
                              </Box>
                              <Box component="span" sx={{ color: 'text.secondary' }}> • </Box>
                              <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>
                                {up(c.CityDescription ?? c.cityDescription)}
                              </Box>
                              <Box component="span" sx={{ color: 'text.secondary' }}> • </Box>
                              <Box component="span" sx={{ color: 'secondary.main', fontWeight: 700 }}>
                                {`TR.${(c.StateCode ?? c.stateCode ?? '-').toString().toUpperCase()}`}
                              </Box>
                            </Box>
                          </TableCell>
                          
                        </TableRow>
                      );
                    }))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default SalesMenu;
